import { useState, useCallback } from 'react';
import { useAuthenticatedFetchWithWorkspace } from '../Keycloak/useAuthenticatedFetchWithWorkspace';

// useAuthenticatedFetch throws `Error("HTTP 422 ...: " + rawBodyText)` on a non-2xx response — pull
// the backend's own `error` message (e.g. the check-operations/verify failure reason) out of that
// raw JSON tail instead of surfacing the whole "HTTP 422 Unprocessable Entity: {...}" string to the user.
const extractErrorMessage = (err: unknown, fallback: string): string => {
    if (!(err instanceof Error)) return fallback;
    const jsonStart = err.message.indexOf('{');
    if (jsonStart === -1) return err.message || fallback;
    try {
        const body = JSON.parse(err.message.slice(jsonStart));
        return body.error || body.message || fallback;
    } catch {
        return err.message || fallback;
    }
};

export interface ValidateCommunicationPayload {
  communicationInputId: number;
  acceptBATVisualization: 0 | 1;
  acceptCGV: 0 | 1;
  needImmediateExtract: 0 | 1;
}

export interface ValidateCommunicationResult {
  success: boolean;
  message: string;
  actionStatusId: number;
}

// POSTs /api/communication/validate — the "Terminer" step of the communication dialog. The backend
// itself calls the external ApiCheckOperation (check-operations/verify) before marking the
// communication validated, so a failed check surfaces here as a thrown error (422).
const useValidateCommunication = () => {
  const { authFetch, isReady } = useAuthenticatedFetchWithWorkspace();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const validateCommunication = useCallback(
    async (payload: ValidateCommunicationPayload): Promise<ValidateCommunicationResult | null> => {
      if (!isReady) return null;
      setIsLoading(true);
      setError('');
      try {
        const result: ValidateCommunicationResult = await authFetch('/communication/validate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        return result;
      } catch (err: unknown) {
        setError(extractErrorMessage(err, "Erreur lors de la vérification de l'opération"));
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [authFetch, isReady]
  );

  return { validateCommunication, isLoading, error };
};

export default useValidateCommunication;
